import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

import { useEntryPointPreload } from './useEntryPointPreload';

export type OnLoad<T> = (obj: { dispose: () => void; reference: T }) => void;

export type UsePressablePreloadRefProps<T> = {
	load: () => T;
	loadOnFocus?: boolean;
	onBeforeLoad?: () => void;
	onLoad: OnLoad<T>;
	preloadOnFocus?: boolean;
};

export function usePressablePreloadRef<T extends { dispose: () => void }>({
	load,
	onLoad,
	onBeforeLoad,
	loadOnFocus = false,
	preloadOnFocus = false,
}: UsePressablePreloadRefProps<T>): (element: HTMLElement | null) => void {
	const nodeRef = useRef<HTMLElement | null>(null);
	const eventListeners = useRef<UnbindFn[]>([]);

	const { preloadReference, cancelPreload, eagerLoad } = useEntryPointPreload(load);

	const clickListener = useMemo(() => {
		return () => {
			if (onBeforeLoad) {
				onBeforeLoad();
			}
			onLoad(eagerLoad());
		};
	}, [onBeforeLoad, onLoad, eagerLoad]);

	const mouseDownListener = useMemo(() => {
		return () => {
			eagerLoad();
		};
	}, [eagerLoad]);

	const cleanUpEventListeners = useCallback(() => {
		eventListeners.current.forEach((unbind) => {
			unbind();
		});
		eventListeners.current = [];
	}, []);

	const addEventListener = useCallback(
		(element: HTMLElement, type: string, callback: EventListener) => {
			const unbind = bind(element, {
				type,
				listener: callback,
			});

			eventListeners.current.push(unbind);
		},
		[],
	);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cancelPreload();
					cleanUpEventListeners();
				}
				if (element) {
					addEventListener(element, 'click', clickListener);
					addEventListener(element, 'mousedown', mouseDownListener);
					addEventListener(element, 'mouseenter', preloadReference);
					addEventListener(element, 'mouseleave', cancelPreload);
					if (loadOnFocus) {
						addEventListener(element, 'focusin', clickListener);
					} else if (preloadOnFocus) {
						addEventListener(element, 'focusin', preloadReference);
						addEventListener(element, 'focusout', cancelPreload);
					}
				}
				// Update the ref with the new element
				nodeRef.current = element;
			}
		},
		[
			clickListener,
			mouseDownListener,
			preloadReference,
			cancelPreload,
			loadOnFocus,
			addEventListener,
			cleanUpEventListeners,
			preloadOnFocus,
		],
	);

	useEffect(() => {
		return () => {
			cleanUpEventListeners();
		};
	}, [cleanUpEventListeners]);

	return refCallback;
}
